import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api as axios, API_URL } from "../api/api";
import { SnackbarContext } from "../App";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Search,
  MapPin,
  Building,
  Briefcase,
  Users,
  Mail,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Globe,
  Plus,
  Download,
  ChevronRight,
  Database,
  X,
  Save,
  Clock,
  Tag as TagIcon,
  Activity,
  Key,
  User,
  Bookmark,
  AlertTriangle,
  Lock,
  Unlock,
  Loader2,
  Columns,
  RefreshCw,
  Filter,
} from "lucide-react";
import ScoreBadge from "./ui/ScoreBadge";
import StatusBadge from "./ui/StatusBadge";
import { Button } from "./ui/button";
import { RoleCombobox } from "./ui/RoleCombobox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
} from "./ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import DataTable from "./ui/DataTable";
import SelectFilter from "./ui/SelectFilter";
import { useWorkspaceCredits } from "../contexts/CreditsContext";
import { useEntitlements } from "../contexts/EntitlementsContext";
import { useEntitlementBlockerModal } from "../contexts/EntitlementBlockerModalContext";
import { formatNumber, normalizeApiError } from "../lib/i18n";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "./ui/sheet";


const CellTooltip = ({ tooltip, children }) => (
  <Tooltip>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent>{tooltip}</TooltipContent>
  </Tooltip>
);

const API_BASE_URL = API_URL;

export default function GlobalContacts() {
  const showSnackbar = useContext(SnackbarContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refreshCredits: refreshWorkspaceCredits } = useWorkspaceCredits();
  const { getRemainingCredits, refreshEntitlements } = useEntitlements();
  const { showEntitlementBlocker, openBlockerModal } =
    useEntitlementBlockerModal();
  const unlockEmailCreditCost = 10;
  const maxDiscoverySelectionSize = 1000000;
  const notifyError = (error, fallbackKey = "errors.generic") =>
    showSnackbar(normalizeApiError(error, t, fallbackKey), "error");

  // Sprint 4 & Density Refactor Search State
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("q") || "";
  const titleFilter = searchParams.get("title") || "";
  const seniority = searchParams.get("seniority") || "any";
  const func = searchParams.get("function") || "any";
  const includeRelatedRoles = searchParams.get("relatedRoles") === "true";
  const location = searchParams.get("location") || "";
  const companyName = searchParams.get("companyName") || "";
  const companyIndustry = searchParams.get("companyIndustry") || "any";
  const companySize = searchParams.get("companySize") || "any";
  const minIntent = searchParams.get("minIntent") || "any";
  const verificationStatus = searchParams.get("verificationStatus") || "any";
  const hasEmail = searchParams.get("hasEmail") === "true";
  const hasPhone = searchParams.get("hasPhone") === "true";
  const hasLinkedin = searchParams.get("hasLinkedin") === "true";
  const excludeAdopted = searchParams.get("excludeAdopted") === "true";
  const sortField = searchParams.get("sortField") || "confidenceScore";
  const sortOrder = searchParams.get("sortOrder") || "desc";

  const updateSearchParam = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value && value !== "any") {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
    setPage(1);
    setSelectedPeople([]);
  };

  const clearAllFilters = () => {
    setSearchParams(new URLSearchParams());
    setPage(1);
    setSelectedPeople([]);
  };

  // Sprint 6 Views State
  const [savedViews, setSavedViews] = useState([]);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [newViewName, setNewViewName] = useState("");
  const [isViewsLoaded, setIsViewsLoaded] = useState(false);
  const activeViewId = searchParams.get("viewId");

  const fetchViews = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/global-pool/views`);
      setSavedViews(res.data.data);
      setIsViewsLoaded(true);
    } catch (err) {
      console.error("Failed to fetch views", err);
    }
  }, []);

  useEffect(() => {
    fetchViews();
  }, [fetchViews]);

  // Gracefully handle dead/deleted view links
  useEffect(() => {
    if (activeViewId && isViewsLoaded) {
      const exists = savedViews.some((v) => v._id === activeViewId);
      if (!exists) {
        showSnackbar(
          t("contacts.global.savedViewMissing"),
          "warning",
        );
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("viewId");
        setSearchParams(newParams, { replace: true });
      }
    }
  }, [
    activeViewId,
    isViewsLoaded,
    savedViews,
    searchParams,
    setSearchParams,
    showSnackbar,
    t,
  ]);

  const serializeCurrentState = () => {
    const filters = {};
    if (search) filters.q = search;
    if (titleFilter) filters.title = titleFilter;
    if (seniority && seniority !== "any") filters.seniority = seniority;
    if (func && func !== "any") filters.function = func;
    if (includeRelatedRoles) filters.relatedRoles = true;
    if (location) filters.location = location;
    if (companyName) filters.companyName = companyName;
    if (companyIndustry && companyIndustry !== "any")
      filters.companyIndustry = companyIndustry;
    if (companySize && companySize !== "any") filters.companySize = companySize;
    if (minIntent && minIntent !== "any")
      filters.minIntent = parseInt(minIntent, 10);
    if (verificationStatus && verificationStatus !== "any")
      filters.verificationStatus = verificationStatus;
    if (hasEmail) filters.hasEmail = true;
    if (hasPhone) filters.hasPhone = true;
    if (hasLinkedin) filters.hasLinkedin = true;
    if (excludeAdopted) filters.excludeAdopted = true;
    return filters;
  };

  const applyView = (view) => {
    const newParams = new URLSearchParams();
    newParams.set("viewId", view._id);
    Object.entries(view.filters || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== false)
        newParams.set(k, String(v));
    });
    setSearchParams(newParams);
    setPage(1);
    setSelectedPeople([]); // Prevent ghost executions on segment hop
  };

  const handleSaveView = async () => {
    if (!newViewName) return;
    try {
      const filters = serializeCurrentState();
      const res = await axios.post(`${API_BASE_URL}/api/global-pool/views`, {
        name: newViewName,
        filters,
      });
      await fetchViews();
      setIsSaveModalOpen(false);
      setNewViewName("");
      applyView(res.data.data);
      showSnackbar(t("contacts.global.viewSaved"), "success");
    } catch (e) {
      notifyError(e, "contacts.global.viewSaveFailed");
    }
  };

  const handleUpdateView = async () => {
    if (!activeViewId) return;
    try {
      const filters = serializeCurrentState();
      await axios.put(`${API_BASE_URL}/api/global-pool/views/${activeViewId}`, {
        filters,
      });
      await fetchViews();
      setIsUpdateModalOpen(false);
      showSnackbar(t("contacts.global.viewUpdated"), "success");
    } catch (e) {
      notifyError(e, "contacts.global.viewUpdateFailed");
    }
  };

  const activeView = savedViews.find((v) => v._id === activeViewId);
  let isDirty = false;
  if (activeView) {
    const currentHash = JSON.stringify(serializeCurrentState());
    const savedHash = JSON.stringify(activeView.filters || {});
    isDirty = currentHash !== savedHash;
  }

  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeople, setSelectedPeople] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState([
    "name",
    "email",
    "title",
    "company",
    "intelligence",
  ]);
  const [unlockModal, setUnlockModal] = useState({
    isOpen: false,
    contactId: null,
    isBulk: false,
  });
  const [unlockModalIsLoading, setUnlockModalIsLoading] = useState(false);

  // Layout State
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const activeContactId = searchParams.get("contactId");
  const [drawerError, setDrawerError] = useState(false);
  const [drawerData, setDrawerData] = useState(null);
  const [isDrawerHydrating, setIsDrawerHydrating] = useState(false);

  // Sprint 7 Bulk Actions State
  const [isSequenceModalOpen, setIsSequenceModalOpen] = useState(false);
  const [isOwnerModalOpen, setIsOwnerModalOpen] = useState(false);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [isAddToListModalOpen, setIsAddToListModalOpen] = useState(false);
  const [isAddToGroupModalOpen, setIsAddToGroupModalOpen] = useState(false);
  const [isBulkLoading, setIsBulkLoading] = useState(false);
  const [listActionLoading, setListActionLoading] = useState(false);
  const [groupActionLoading, setGroupActionLoading] = useState(false);

  // Pickers data
  const [sequences, setSequences] = useState([]);
  const [users, setUsers] = useState([]);
  const [prospectLists, setProspectLists] = useState([]);
  const [groups, setGroups] = useState([]);
  const [isProspectListsLoading, setIsProspectListsLoading] = useState(false);
  const [isGroupsLoading, setIsGroupsLoading] = useState(false);

  const [selectedSequenceId, setSelectedSequenceId] = useState("");
  const [selectedOwnerId, setSelectedOwnerId] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [listNameInput, setListNameInput] = useState("");
  const [listVisibility, setListVisibility] = useState("PRIVATE");

  useEffect(() => {
    const fetchBulkData = async () => {
      try {
        const [seqRes, userRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/sequences`),
          axios.get(`${API_BASE_URL}/api/workspace/members`),
        ]);
        setSequences(seqRes.data || []);
        setUsers(userRes.data || []);
      } catch (e) {
        console.error("Failed to load pickers", e);
      }
    };
    fetchBulkData();
  }, []);

  const isAnyModalOpen =
    isAddToListModalOpen ||
    isSequenceModalOpen ||
    isOwnerModalOpen ||
    isTagModalOpen ||
    unlockModal.isOpen ||
    isSaveModalOpen ||
    isUpdateModalOpen;

  const performBulkAction = async (actionFn, options = {}) => {
    const { refreshCredits = false, successMessage = null } = options;
    const targetIds =
      selectedPeople.length > 0
        ? selectedPeople
        : activeContactId
          ? [activeContactId]
          : [];
    if (targetIds.length === 0) return;
    if (targetIds.length > 1000) {
      showSnackbar(
        t("contacts.global.maxBatchSize"),
        "error",
      );
      return;
    }
    setIsBulkLoading(true);
    let didSucceed = false;
    try {
      await actionFn(targetIds);
      didSucceed = true;
      showSnackbar(
        successMessage ||
        t("contacts.global.processedSuccess", { count: targetIds.length }),
        "success",
      );
      setSelectedPeople([]); // Clear selection on success
    } catch (e) {
      notifyError(e, "contacts.global.actionFailed");
    } finally {
      if (refreshCredits && didSucceed) {
        await refreshWorkspaceCredits({ silent: true });
      }
      setIsBulkLoading(false);
    }
  };

  const handleBulkSequence = () => {
    if (!selectedSequenceId) return;
    performBulkAction(
      async (targetIds) => {
        await axios.post(`${API_BASE_URL}/api/global-pool/people/enroll`, {
          personIds: targetIds,
          sequenceId: selectedSequenceId,
        });
        setIsSequenceModalOpen(false);
        setSelectedSequenceId("");
      },
      { refreshCredits: true },
    );
  };

  const handleBulkOwner = () => {
    if (!selectedOwnerId) return;
    performBulkAction(
      async (targetIds) => {
        await axios.post(`${API_BASE_URL}/api/global-pool/people/bulk-adopt`, {
          personIds: targetIds,
          options: { ownerId: selectedOwnerId },
        });
        setIsOwnerModalOpen(false);
        setSelectedOwnerId("");
      },
      { refreshCredits: true },
    );
  };

  const handleBulkTag = () => {
    if (!tagInput) return;
    performBulkAction(
      async (targetIds) => {
        const tags = tagInput
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean);
        await axios.post(`${API_BASE_URL}/api/global-pool/people/bulk-adopt`, {
          personIds: targetIds,
          options: { tags },
        });
        setIsTagModalOpen(false);
        setTagInput("");
      },
      { refreshCredits: true },
    );
  };

  const fetchProspectLists = useCallback(async () => {
    setIsProspectListsLoading(true);
    try {
      const res = await axios.get("/api/prospect-lists");
      setProspectLists(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (e) {
      console.error("Failed to fetch prospect lists", e);
      showSnackbar(t("contacts.global.messages.listLoadFailed"), "error");
    } finally {
      setIsProspectListsLoading(false);
    }
  }, [showSnackbar, t]);

  const openAddToListModal = () => {
    if (selectedPeople.length === 0) return;
    setListNameInput("");
    setListVisibility("PRIVATE");
    setIsAddToListModalOpen(true);
    fetchProspectLists();
  };

  const handleAddToList = async () => {
    const listName = listNameInput.trim();
    if (!listName) {
      showSnackbar(t("discovery.messages.listNameRequired"), "error");
      return;
    }

    setListActionLoading(true);
    try {
      let listId;
      const existing = prospectLists.find(
        (list) => list.name?.toLowerCase() === listName.toLowerCase(),
      );

      if (existing) {
        listId = existing._id;
      } else {
        const listRes = await axios.post("/api/prospect-lists", {
          name: listName,
          visibility: listVisibility,
        });
        listId = listRes.data?.data?._id;
      }

      const addRes = await axios.post(`/api/prospect-lists/${listId}/add`, {
        personIds: selectedPeople,
      });

      const { added, alreadyInList } = addRes.data;

      if (alreadyInList > 0 && added > 0) {
        showSnackbar(
          t("discovery.messages.addedWithDuplicates", {
            added: added,
            already: alreadyInList,
            name: listName,
          }),
          "success",
        );
      } else if (alreadyInList > 0 && added === 0) {
        showSnackbar(
          t("discovery.messages.allDuplicates", {
            count: alreadyInList,
            name: listName,
          }),
          "info",
        );
      } else {
        showSnackbar(
          t("discovery.messages.addedToList", {
            count: added ?? selectedPeople.length,
            name: listName,
          }),
          "success",
        );
      }
      setSelectedPeople([]);
      setIsAddToListModalOpen(false);
      setListNameInput("");
      fetchProspectLists();
    } catch (e) {
      showSnackbar(
        t("discovery.messages.addToListFailed", {
          message: e?.message || t("errors.generic"),
        }),
        "error",
      );
    } finally {
      setListActionLoading(false);
    }
  };

  const fetchGroups = useCallback(async () => {
    setIsGroupsLoading(true);
    try {
      const res = await axios.get("/api/groups?limit=100");
      // Filter for STATIC groups only since DYNAMIC ones handle their own membership
      setGroups((res.data?.data || []).filter(g => g.type === 'STATIC'));
    } catch (e) {
      console.error("Failed to fetch groups", e);
    } finally {
      setIsGroupsLoading(false);
    }
  }, []);

  const handleAddToGroup = async () => {
    if (!selectedGroupId) {
      showSnackbar(t("discovery.messages.groupRequired"), "error");
      return;
    }

    const group = groups.find(g => g._id === selectedGroupId);

    try {
      setGroupActionLoading(true);
      const res = await axios.post(`/api/groups/${selectedGroupId}/members/bulk-adopt`, {
        personIds: selectedPeople,
      });

      const { added_to_group } = res.data;
      
      showSnackbar(
        t("discovery.messages.addedToGroup", {
          total: selectedPeople.length,
          count: added_to_group,
          name: group?.name
        }),
        "success"
      );

      setSelectedPeople([]);
      setIsAddToGroupModalOpen(false);
      setSelectedGroupId("");
    } catch (e) {
      notifyError(e, "discovery.messages.addToGroupFailed");
    } finally {
      setGroupActionLoading(false);
    }
  };

  const copySelectedValues = async ({
    getValue,
    emptyMessage,
    successMessage,
  }) => {
    const selectedRows = people.filter((person) =>
      selectedPeople.includes(person._id),
    );
    const values = Array.from(
      new Set(
        selectedRows
          .map(getValue)
          .map((value) => (typeof value === "string" ? value.trim() : value))
          .filter(Boolean),
      ),
    );

    if (values.length === 0) {
      showSnackbar(emptyMessage, "info");
      return;
    }

    try {
      await navigator.clipboard.writeText(values.join("\n"));
      showSnackbar(
        t(successMessage, { count: values.length }),
        "success",
      );
    } catch (e) {
      notifyError(e, "contacts.global.messages.copyFailed");
    }
  };

  const handleCopyEmails = () =>
    copySelectedValues({
      getValue: (person) => person.canonicalEmail,
      emptyMessage: t("contacts.global.messages.noEmailsToCopy"),
      successMessage: "contacts.global.messages.emailsCopied",
    });

  const handleCopyLinkedin = () =>
    copySelectedValues({
      getValue: (person) =>
        person.linkedinUrl || person.socialProfiles?.linkedin,
      emptyMessage: t("contacts.global.messages.noLinkedinToCopy"),
      successMessage: "contacts.global.messages.linkedinCopied",
    });

  const handleExportCSV = async () => {
    if (selectedPeople.length === 0 || selectedPeople.length > 5000) {
      showSnackbar(t("contacts.global.exportSelectionError"), "error");
      return;
    }
    setIsBulkLoading(true);
    showSnackbar(t("contacts.global.generatingExport"), "info");
    let didSucceed = false;
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/exports/discovery`,
        { personIds: selectedPeople },
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `discovery_export_${new Date().getTime()}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      didSucceed = true;
      setSelectedPeople([]); // Clear on success
    } catch (e) {
      handleEntitlementError(e, "Export failed.", {
        action: "discovery-export",
      });
    } finally {
      if (didSucceed) {
        await refreshWorkspaceCredits({ silent: true });
      }
      setIsBulkLoading(false);
    }
  };

  useEffect(() => {
    if (activeContactId) {
      setIsDrawerHydrating(true);
      setDrawerError(false);
      axios
        .get(`${API_BASE_URL}/api/global-pool/people/${activeContactId}`)
        .then((res) => setDrawerData(res.data.data))
        .catch((err) => {
          console.error("Failed to hydrate.", err);
          setDrawerError(true);
        })
        .finally(() => setIsDrawerHydrating(false));
    } else {
      setDrawerData(null);
      setDrawerError(false);
    }
  }, [activeContactId]);

  const localContact = React.useMemo(
    () => people.find((p) => p._id === activeContactId),
    [people, activeContactId],
  );
  const displayContact = drawerData || localContact;

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [limit, setLimit] = useState(25);

  // Modals
  const showSnackbarRef = useRef(showSnackbar);
  useEffect(() => {
    showSnackbarRef.current = showSnackbar;
  }, [showSnackbar]);

  const buildSearchBody = useCallback(
    (pageOverride, limitOverride) => ({
      page: pageOverride,
      limit: limitOverride,
      title: titleFilter || undefined,
      includeRelatedRoles: includeRelatedRoles || undefined,
      location: location || undefined,
      companyName: companyName || undefined,
      seniorityLevel: seniority !== "any" ? seniority : undefined,
      industry: companyIndustry !== "any" ? companyIndustry : undefined,
      companySize: companySize !== "any" ? companySize : undefined,
      minIntent: minIntent !== "any" ? parseInt(minIntent, 10) : undefined,
      verificationStatus:
        verificationStatus !== "any" ? verificationStatus : undefined,
      hasEmail: hasEmail ? "true" : undefined,
      excludeAdopted: excludeAdopted ? "true" : undefined,
      sortField,
      sortOrder,
      ...(search && { q: search }),
    }),
    [
      titleFilter,
      includeRelatedRoles,
      location,
      companyName,
      seniority,
      companyIndustry,
      companySize,
      minIntent,
      verificationStatus,
      hasEmail,
      excludeAdopted,
      sortField,
      sortOrder,
      search,
    ],
  );

  const fetchMatchingPeople = useCallback(
    async ({ desiredCount, maxPerCompany = null }) => {
      const safeDesiredCount = Math.max(
        1,
        Math.min(desiredCount, maxDiscoverySelectionSize),
      );
      const collectedPeople = [];
      const seenIds = new Set();
      const companyCounter = new Map();
      let currentPage = 1;
      let fetchedTotalPages = 1;

      while (
        collectedPeople.length < safeDesiredCount &&
        currentPage <= fetchedTotalPages
      ) {
        const pageSize = Math.min(250, safeDesiredCount);
        const res = await axios.post(
          `${API_BASE_URL}/api/global-pool/people/search`,
          buildSearchBody(currentPage, pageSize),
        );
        const pageResults = Array.isArray(res.data?.results)
          ? res.data.results
          : [];
        const total = res.data?.stats?.total || 0;
        fetchedTotalPages = Math.max(1, Math.ceil(total / pageSize));

        for (const person of pageResults) {
          if (!person?._id || seenIds.has(person._id)) continue;

          if (maxPerCompany && Number(maxPerCompany) > 0) {
            const companyKey =
              person.companyMasterId || person.companyName || "__unknown__";
            const currentCount = companyCounter.get(companyKey) || 0;
            if (currentCount >= Number(maxPerCompany)) continue;
            companyCounter.set(companyKey, currentCount + 1);
          }

          seenIds.add(person._id);
          collectedPeople.push(person);

          if (collectedPeople.length >= safeDesiredCount) break;
        }

        if (pageResults.length === 0) break;
        currentPage += 1;
      }

      return collectedPeople;
    },
    [buildSearchBody],
  );

  const fetchGlobalPool = useCallback(async () => {
    setLoading(true);
    try {
      const body = buildSearchBody(page, limit);
      const res = await axios.post(
        `${API_BASE_URL}/api/global-pool/people/search`,
        body,
      );

      // Artificial delay to show premium loading skeleton locally
      setTimeout(() => {
        setPeople(res.data.results || []);
        const total = res.data.stats?.total || 0;
        setTotalPages(Math.ceil(total / limit) || 1);
        setTotalRecords(total);
        setLoading(false);
      }, 600);
    } catch (error) {
      console.error("Error fetching global pool:", error);
      showSnackbarRef.current(t("contacts.global.loadError"), "error");
      setPeople([]);
      setLoading(false);
    }
  }, [
    page,
    limit,
    buildSearchBody,
    t,
  ]);

  const handleRefreshData = () => {
    fetchGlobalPool();
    refreshWorkspaceCredits({ silent: true });
  };

  useEffect(() => {
    fetchGlobalPool();
  }, [fetchGlobalPool]);

  // Credit-gated email unlock handlers
  const getRemainingEmailCredits = () => getRemainingCredits("emailCredits");
  const getKnownPersonById = (personId) =>
    people.find((person) => person._id === personId) || null;
  function handleEntitlementError(err, fallbackMessage, context = {}) {
    const handled = showEntitlementBlocker(err, {
      context: { surface: "discovery", ...context },
    });
    if (handled) return true;
    showSnackbar(normalizeApiError(err, t, fallbackMessage), "error");
    return false;
  }

  const openUnlockModal = (personId, e) => {
    if (e) e.stopPropagation();
    setUnlockModal({ isOpen: true, contactId: personId, isBulk: false });
  };

  const confirmUnlock = async () => {
    const personId = unlockModal.contactId;
    const person = getKnownPersonById(personId);
    const isAlreadyUnlocked = !!person?.isUnlocked;
    const remainingEmailCredits = getRemainingEmailCredits();
    const hasSufficientCredits =
      remainingEmailCredits === Infinity ||
      remainingEmailCredits >= unlockEmailCreditCost;
    if (!isAlreadyUnlocked && !hasSufficientCredits) {
      openBlockerModal({
        blockerCategory: "INSUFFICIENT_CREDITS",
        details: {
          creditKey: "emailCredits",
          planCode: undefined,
          remaining: remainingEmailCredits,
          requiredAmount: unlockEmailCreditCost,
        },
        message: t("contacts.global.insufficientCreditsUnlock"),
        context: { surface: "discovery", action: "unlock-email" },
      });
      await refreshEntitlements({ silent: true });
      return;
    }
    setUnlockModalIsLoading(true);
    let didSucceed = false;
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/global-pool/people/${personId}/unlock-email`,
      );
      const { status, email } = res.data;
      if (status === "SUCCESS" || status === "ALREADY_UNLOCKED") {
        setPeople((prev) =>
          prev.map((p) =>
            p._id === personId
              ? {
                ...p,
                canonicalEmail: email,
                isUnlocked: true,
                isAdopted: true,
              }
              : p,
          ),
        );
        if (activeContactId === personId) {
          setDrawerData((prev) =>
            prev ? { ...prev, canonicalEmail: email, isUnlocked: true } : null,
          );
        }
        showSnackbar(
          status === "SUCCESS"
            ? t("contacts.global.unlockSuccess")
            : t("contacts.global.unlockAlreadyDone"),
          "success",
        );
        setUnlockModal({ isOpen: false, contactId: null, isBulk: false });
        didSucceed = true;
      }
    } catch (err) {
      handleEntitlementError(err, t("contacts.global.unlockFailed"), {
        action: "unlock-email",
      });
    } finally {
      if (didSucceed) {
        await refreshWorkspaceCredits({ silent: true });
      }
      setUnlockModalIsLoading(false);
    }
  };

  const openBulkUnlockModal = () => {
    if (!selectedPeople.length) return;
    if (selectedPeople.length > 400) {
      showSnackbar(t("contacts.global.maxBulkUnlock"), "error");
      return;
    }
    setUnlockModal({ isOpen: true, isBulk: true, contactId: null });
  };

  const confirmBulkUnlock = async () => {
    const remainingEmailCredits = getRemainingEmailCredits();
    const hasAnyLockedCandidate = selectedPeople.some((personId) => {
      const person = getKnownPersonById(personId);
      return !person?.isUnlocked;
    });
    if (
      hasAnyLockedCandidate &&
      remainingEmailCredits !== Infinity &&
      remainingEmailCredits <= 0
    ) {
      openBlockerModal({
        blockerCategory: "INSUFFICIENT_CREDITS",
        details: {
          creditKey: "emailCredits",
          remaining: remainingEmailCredits,
          requiredAmount: unlockEmailCreditCost,
        },
        message: t("contacts.global.insufficientCreditsBulkUnlock"),
        context: { surface: "discovery", action: "bulk-unlock" },
      });
      await refreshEntitlements({ silent: true });
      return;
    }

    setUnlockModalIsLoading(true);
    let didSucceed = false;
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/global-pool/people/bulk-unlock`,
        { personIds: selectedPeople },
      );
      const { diagnostics } = res.data;
      if (diagnostics?.emails) {
        setPeople((prev) =>
          prev.map((p) => {
            if (diagnostics.emails[p._id]) {
              return {
                ...p,
                canonicalEmail: diagnostics.emails[p._id],
                isUnlocked: true,
                isAdopted: true,
              };
            }
            return p;
          }),
        );
      }
      showSnackbar(
        t("contacts.global.bulkUnlockSuccess", {
          count: diagnostics.totalCreditsCharged,
        }),
        "success",
      );
      setSelectedPeople([]);
      setUnlockModal({ isOpen: false, contactId: null, isBulk: false });
      didSucceed = true;
    } catch (err) {
      handleEntitlementError(err, t("contacts.global.bulkUnlockFailed"), {
        action: "bulk-unlock",
      });
    } finally {
      if (didSucceed) {
        await refreshWorkspaceCredits({ silent: true });
      }
      setUnlockModalIsLoading(false);
    }
  };

  const handleConfirmUnlock = () => {
    if (unlockModal.isBulk) {
      confirmBulkUnlock();
    } else {
      confirmUnlock();
    }
  };

  const handleCloseDrawer = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("contactId");
    setSearchParams(newParams);
  };

  const handleRowClick = (contact) => {
    if (activeContactId === contact._id) {
      handleCloseDrawer();
    } else {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("contactId", contact._id);
      setSearchParams(newParams);
    }
  };

  const remainingEmailCredits = getRemainingEmailCredits();
  const remainingEmailCreditsLabel =
    remainingEmailCredits === Infinity
      ? t("common.unlimited")
      : formatNumber(remainingEmailCredits || 0);

  const allColumns = [
    {
      id: "name",
      header: t("contacts.global.table.name"),
      accessor: "lastName",
      sortable: true,
      render: (p) => (
        <div className="flex flex-col py-1 min-w-[140px]">
          <CellTooltip tooltip={`${p.firstName} ${p.lastName}`}>
            <span className="text-sm font-bold tracking-tight text-foreground hover:text-primary transition-colors cursor-pointer line-clamp-1">
              {p.firstName} {p.lastName}
            </span>
          </CellTooltip>
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            {p.globalVerificationStatus === "VERIFIED" && (
              <CellTooltip tooltip={t("contacts.global.tooltips.verifiedEmail")}>
                <div>
                  <StatusBadge variant="success">{t("status.verified")}</StatusBadge>
                </div>
              </CellTooltip>
            )}
            {p.globalVerificationStatus === "RISKY" && (
              <CellTooltip tooltip={t("contacts.global.tooltips.riskyEmail")}>
                <div>
                  <StatusBadge variant="warning">{t("status.risky")}</StatusBadge>
                </div>
              </CellTooltip>
            )}
          </div>
        </div>
      ),
    },
    {
      id: "email",
      header: t("contacts.global.table.email"),
      accessor: "canonicalEmail",
      sortable: false,
      render: (p) => (
        <div className="flex flex-col py-1 min-w-[200px]">
          {p.isUnlocked && p.canonicalEmail ? (
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-1.5 text-xs text-foreground font-bold">
                <Unlock className="w-3.5 h-3.5 text-emerald-500" />{" "}
                {p.canonicalEmail}
              </div>
              <div className="flex items-center gap-1.5">
                <CellTooltip tooltip={t("contacts.global.tooltips.emailUnlocked")}>
                  <div>
                    <StatusBadge variant="success">{t("contacts.global.labels.unlocked")}</StatusBadge>
                  </div>
                </CellTooltip>
                <CellTooltip tooltip={t("contacts.global.tooltips.savedToCrm")}>
                  <div>
                    <StatusBadge variant="info">{t("contacts.global.labels.saved")}</StatusBadge>
                  </div>
                </CellTooltip>
              </div>
            </div>
          ) : p.emailAvailable ? (
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground font-bold tracking-wider">
                <Lock className="w-3.5 h-3.5 text-amber-500" /> {t("contacts.global.labels.verifiedEmailAvailable")}
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge variant="warning">{t("contacts.global.labels.locked")}</StatusBadge>
                <CellTooltip tooltip={t("contacts.global.tooltips.unlockCredits", { count: unlockEmailCreditCost })}>
                  <button
                    onClick={(e) => openUnlockModal(p._id, e)}
                    className="flex items-center gap-1 px-1.5 py-0.5 bg-primary/10 hover:bg-primary/20 text-primary text-[9px] font-black uppercase tracking-widest rounded-md border border-primary/20 transition-all hover:scale-105 active:scale-95 ml-1"
                  >
                    <Key className="w-2.5 h-2.5" /> {t("contacts.global.actions.unlockForCredits", { count: unlockEmailCreditCost })}
                  </button>
                </CellTooltip>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground/50 font-bold tracking-wider">
              <Mail className="w-3.5 h-3.5 opacity-50" /> {t("contacts.global.labels.noVerifiedEmail")}
            </div>
          )}
        </div>
      ),
    },
    {
      id: "title",
      header: t("contacts.global.table.role"),
      accessor: "title",
      sortable: true,
      render: (p) => (
        <CellTooltip tooltip={p.title || "-"}>
          <div className="text-xs font-bold text-foreground line-clamp-2 pr-4">
            {p.title || "-"}
          </div>
        </CellTooltip>
      ),
    },
    {
      id: "company",
      header: t("contacts.global.table.company"),
      accessor: "companyName",
      sortable: true,
      render: (p) => (
        <div className="flex flex-col py-1 min-w-[120px]">
          <CellTooltip tooltip={p.companyName || "-"}>
            <div className="text-xs font-bold text-foreground line-clamp-1">
              <Building className="w-3.5 h-3.5 text-primary opacity-80 inline mr-1" />
              {p.companyName || "-"}
            </div>
          </CellTooltip>
        </div>
      ),
    },
    {
      id: "location",
      header: t("contacts.global.table.location"),
      accessor: "location",
      sortable: true,
      render: (p) =>
        p.location ? (
          <div className="text-xs text-muted-foreground font-medium flex items-center gap-1.5 min-w-[100px]">
            <MapPin className="w-3.5 h-3.5" />
            {p.location.split(",")[0]}
          </div>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      id: "seniority",
      header: t("contacts.global.table.seniority"),
      accessor: "seniorityLevel",
      sortable: true,
      render: (p) =>
        p.seniorityLevel ? (
          <StatusBadge variant="outline">{p.seniorityLevel}</StatusBadge>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      id: "industry",
      header: t("contacts.global.table.industry"),
      accessor: "industry",
      sortable: true,
      render: (p) =>
        p.industry ? (
          <div className="text-xs font-medium text-muted-foreground line-clamp-1 min-w-[100px]">
            {p.industry}
          </div>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      id: "intelligence",
      header: t("contacts.global.table.intelligence"),
      accessor: "confidenceScore",
      sortable: true,
      render: (p) => (
        <div className="flex flex-col gap-2 py-1 min-w-[120px]">
          <CellTooltip tooltip={t("contacts.global.tooltips.matchRelevance")}>
            <div className="flex items-center gap-2 cursor-help">
              <ScoreBadge score={p.confidenceScore || 0} />
              <span className="text-[9px] font-black uppercase tracking-[0.2em] text-muted-foreground/60">
                {t("contacts.global.labels.matchIntegrity")}
              </span>
            </div>
          </CellTooltip>
          <div className="flex items-center gap-1.5 mt-0.5">
            {p.intentScore >= 80 && (
              <CellTooltip tooltip={t("contacts.global.tooltips.highIntent")}>
                <div>
                  <StatusBadge variant="danger">{t("contacts.global.labels.hotIntent")}</StatusBadge>
                </div>
              </CellTooltip>
            )}
            {p.intentScore >= 50 && p.intentScore < 80 && (
              <CellTooltip tooltip={t("contacts.global.tooltips.warmIntent")}>
                <div>
                  <StatusBadge variant="warning">{t("contacts.global.labels.warm")}</StatusBadge>
                </div>
              </CellTooltip>
            )}
            {p.isAdopted && (
              <CellTooltip tooltip={t("contacts.global.tooltips.existsInCrm")}>
                <div>
                  <StatusBadge variant="info">{t("contacts.global.labels.inCrm")}</StatusBadge>
                </div>
              </CellTooltip>
            )}
          </div>
        </div>
      ),
    },
  ];

  const toggleColumn = (colId) => {
    setVisibleColumns((prev) =>
      prev.includes(colId)
        ? prev.filter((id) => id !== colId)
        : [...prev, colId],
    );
  };

  const handleAdvancedSelection = async ({
    mode,
    peopleCount,
    maxPerCompany,
  }) => {
    const scopedPeople = Array.isArray(people) ? people : [];

    if (scopedPeople.length === 0) {
      setSelectedPeople([]);
      return;
    }

    let nextSelection = [];

    if (mode === "number") {
      const requestedCount = Math.max(1, Number(peopleCount) || 1);
      const needsRemoteFetch =
        requestedCount > scopedPeople.length ||
        (maxPerCompany && Number(maxPerCompany) > 0);

      if (needsRemoteFetch) {
        const matchedPeople = await fetchMatchingPeople({
          desiredCount: requestedCount,
          maxPerCompany,
        });
        nextSelection = matchedPeople.map((person) => person._id);
      } else {
        nextSelection = scopedPeople
          .slice(0, requestedCount)
          .map((person) => person._id);
      }
    } else if (mode === "all") {
      const requestedCount = Math.min(totalRecords, maxDiscoverySelectionSize);
      const matchedPeople = await fetchMatchingPeople({
        desiredCount: requestedCount,
      });
      nextSelection = matchedPeople.map((person) => person._id);

      if (totalRecords > maxDiscoverySelectionSize) {
        showSnackbar(
          t("contacts.global.messages.selectionCapped", {
            count: maxDiscoverySelectionSize,
          }),
          "info",
        );
      }
    } else if (mode === "page") {
      nextSelection = scopedPeople.map((person) => person._id);
    }

    setSelectedPeople(nextSelection);
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex flex-col h-full min-h-0 bg-background overflow-hidden">
        {/* --- Workspace Header --- */}
        <div className="px-6 py-4 border-b border-border/40 bg-card/60 backdrop-blur-xl shrink-0">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center border border-primary/20 shadow-inner">
                <Globe className="w-7 h-7 text-primary animate-pulse" />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-3xl font-black tracking-tighter text-foreground">
                    {t("contacts.global.title")}
                  </h2>
                  <StatusBadge
                    variant="success"
                    className="h-5 px-2 text-[10px] font-black uppercase tracking-widest border-none shadow-sm"
                  >
                    {t("contacts.global.labels.realTime")}
                  </StatusBadge>
                </div>
                <div className="flex items-center gap-3 mt-1.5 font-bold text-xs text-muted-foreground/60 tracking-tight">
                  <span className="flex items-center gap-1.5">
                    <Search className="w-3.5 h-3.5" /> {t("contacts.global.hero.verifiedRecords")}
                  </span>
                  <span className="opacity-30">•</span>
                  <span className="flex items-center gap-1.5">
                    <Activity className="w-3.5 h-3.5" /> {t("contacts.global.hero.intentSignals")}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <CellTooltip tooltip={t("contacts.global.tooltips.refreshDiscovery")}>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleRefreshData}
                  className="h-11 w-11 rounded-xl border-border/40 bg-card hover:bg-muted"
                >
                  <RefreshCw
                    className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                  />
                </Button>
              </CellTooltip>
              {isDirty && (
                <CellTooltip tooltip={t("contacts.global.tooltips.pendingChanges")}>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsSaveModalOpen(true)}
                      className="h-11 px-5 rounded-xl border-dashed border-primary/40 bg-primary/5 text-primary font-black uppercase text-[10px] tracking-widest hover:bg-primary/10"
                    >
                      <Bookmark className="w-3.5 h-3.5 mr-2" /> {t("contacts.global.actions.saveSegment")}
                    </Button>
                  </div>
                </CellTooltip>
              )}
              <Button
                onClick={handleExportCSV}
                variant="outline"
                className="h-11 px-5 rounded-xl border-border/40 font-black uppercase text-[10px] tracking-widest gap-2 bg-card hover:bg-muted"
              >
                <Database className="w-3.5 h-3.5" /> {t("contacts.global.actions.exportPool")}
              </Button>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row flex-1 min-h-0 bg-card overflow-hidden relative">
          <div
            className={`transition-all duration-300 ease-in-out border-r border-border bg-card flex flex-col h-full flex-shrink-0
                        ${isSidebarOpen ? "w-72 lg:w-[320px] translate-x-0" : "w-0 -translate-x-full border-r-0 opacity-0 overflow-hidden"}
                        absolute md:relative z-20 md:z-auto
                    `}
          >
            <div className="flex-1 overflow-y-auto custom-scrollbar flex flex-col pb-10">
              {/* 1. DISCOVERY NAVIGATION */}
              <div className="p-4 border-b border-border/40">
                <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/50 mb-3 px-1">
                  {t("contacts.global.sections.discovery")}
                </h4>
                <div className="space-y-1">
                  <button className="w-full flex items-center justify-between px-3 py-2.5 bg-primary/10 text-primary font-bold text-sm rounded-xl">
                    <span className="flex items-center">
                      <Globe className="w-4 h-4 mr-2" /> {t("contacts.global.title")}
                    </span>
                    <StatusBadge
                      variant="success"
                      className="scale-75 uppercase text-[9px] font-black h-4 px-1"
                    >
                      {t("status.active")}
                    </StatusBadge>
                  </button>
                  <button
                    onClick={() => navigate("/crm/contacts")}
                    className="w-full flex items-center justify-between px-3 py-2.5 text-foreground hover:bg-muted font-bold text-sm rounded-xl transition-colors"
                  >
                    <span className="flex items-center">
                      <Users className="w-4 h-4 mr-2 text-muted-foreground" />{" "}
                      {t("contacts.global.labels.myWorkspace")}
                    </span>
                    <span className="text-[9px] font-black uppercase text-muted-foreground tracking-widest bg-muted/60 border border-border/50 px-2 py-0.5 rounded-md">
                      {t("contacts.global.labels.saved")}
                    </span>
                  </button>
                </div>
              </div>

              {/* 2. SAVED SEGMENTS */}
              <div className="p-4 border-b border-border/40">
                <div className="flex items-center justify-between mb-3 px-1">
                  <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/50">
                    {t("contacts.global.sections.storedSegments")}
                  </h4>
                  {isDirty && activeView && (
                    <div className="flex gap-2">
                      <CellTooltip tooltip={t("contacts.global.tooltips.updateSegment")}>
                        <Save
                          onClick={() => setIsUpdateModalOpen(true)}
                          className="w-3.5 h-3.5 text-amber-500 cursor-pointer hover:opacity-80 transition-opacity"
                        />
                      </CellTooltip>
                      <CellTooltip tooltip={t("contacts.global.tooltips.saveAsNewSegment")}>
                        <Plus
                          onClick={() => setIsSaveModalOpen(true)}
                          className="w-3.5 h-3.5 text-primary cursor-pointer hover:opacity-80 transition-opacity"
                        />
                      </CellTooltip>
                    </div>
                  )}
                  {!isDirty && (
                    <CellTooltip tooltip={t("contacts.global.tooltips.createSegmentView")}>
                      <Plus
                        onClick={() => setIsSaveModalOpen(true)}
                        className="w-3.5 h-3.5 text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                      />
                    </CellTooltip>
                  )}
                </div>

                <div className="space-y-1 max-h-40 overflow-y-auto custom-scrollbar">
                  {savedViews.map((view) => (
                    <button
                      key={view._id}
                      onClick={() => applyView(view)}
                      className={`w-full flex items-center justify-between px-3 py-2 text-sm font-semibold rounded-lg transition-colors group ${activeViewId === view._id ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}
                    >
                      <span className="truncate">{view.name}</span>
                      {activeViewId === view._id && (
                        <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
                      )}
                    </button>
                  ))}
                  {savedViews.length === 0 && (
                    <div className="text-center py-4 border border-dashed border-border/60 rounded-xl bg-muted/10 mx-1">
                      <span className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground/60">
                        {t("contacts.global.empty.noSegments")}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* 3. TAXONOMY FILTERS */}
              <div className="p-4 flex-1 space-y-7">
                <div className="flex items-center justify-between px-1">
                  <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/50">
                    {t("contacts.global.sections.taxonomyFilters")}
                  </h4>
                  <CellTooltip tooltip={t("contacts.global.tooltips.clearFilters")}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearAllFilters}
                      className="h-6 px-2 text-[9px] uppercase font-black tracking-widest text-muted-foreground hover:bg-destructive/5 hover:text-destructive transition-all"
                    >
                      {t("contacts.global.actions.resetAll")}
                    </Button>
                  </CellTooltip>
                </div>

                {/* 1. Identity & Role */}
                <div className="space-y-4">
                  <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60 flex items-center gap-2 border-b border-border/40 pb-2">
                    <User className="w-3 h-3" /> {t("contacts.global.sections.identityRole")}
                  </h4>
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/80 ml-0.5">
                        Canonical Job Title
                      </Label>
                      <RoleCombobox
                        value={titleFilter}
                        onChange={(val) => updateSearchParam("title", val)}
                      />
                      <div className="flex items-center gap-2 mt-1.5 px-0.5">
                        <input
                          type="checkbox"
                          className="rounded border-border w-3.5 h-3.5 text-primary cursor-pointer accent-primary"
                          id="related-roles"
                          checked={includeRelatedRoles}
                          onChange={(e) =>
                            updateSearchParam(
                              "relatedRoles",
                              e.target.checked ? "true" : "",
                            )
                          }
                        />
                        <Label
                          htmlFor="related-roles"
                          className="text-[10px] text-muted-foreground font-bold cursor-pointer uppercase tracking-tight opacity-70 hover:opacity-100 transition-opacity"
                        >
                          Smart Similar Matching
                        </Label>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/80 ml-0.5">
                          Seniority
                        </Label>
                        <Select
                          value={seniority}
                          onValueChange={(val) =>
                            updateSearchParam("seniority", val)
                          }
                        >
                          <SelectTrigger className="h-9 text-[10px] font-bold bg-muted/10 border-border/50 rounded-lg">
                            <SelectValue placeholder="Any" />
                          </SelectTrigger>
                          <SelectContent className="rounded-xl border-border">
                            <SelectItem
                              value="any"
                              className="text-[10px] font-bold uppercase tracking-widest"
                            >
                              Any Level
                            </SelectItem>
                            <SelectItem
                              value="executive"
                              className="text-[10px] font-bold uppercase tracking-widest"
                            >
                              Executive / C-Suite
                            </SelectItem>
                            <SelectItem
                              value="director"
                              className="text-[10px] font-bold uppercase tracking-widest"
                            >
                              Director
                            </SelectItem>
                            <SelectItem
                              value="manager"
                              className="text-[10px] font-bold uppercase tracking-widest"
                            >
                              Manager
                            </SelectItem>
                            <SelectItem
                              value="contributor"
                              className="text-[10px] font-bold uppercase tracking-widest"
                            >
                              Individual Contributor
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/80 ml-0.5">
                          Function
                        </Label>
                        <Select
                          value={func}
                          onValueChange={(val) =>
                            updateSearchParam("function", val)
                          }
                        >
                          <SelectTrigger className="h-9 text-[10px] font-bold bg-muted/10 border-border/50 rounded-lg">
                            <SelectValue placeholder="Any" />
                          </SelectTrigger>
                          <SelectContent className="rounded-xl border-border">
                            <SelectItem
                              value="any"
                              className="text-[10px] font-bold uppercase tracking-widest"
                            >
                              Any Func
                            </SelectItem>
                            <SelectItem
                              value="sales"
                              className="text-[10px] font-bold uppercase tracking-widest"
                            >
                              Sales & BD
                            </SelectItem>
                            <SelectItem
                              value="marketing"
                              className="text-[10px] font-bold uppercase tracking-widest"
                            >
                              Marketing
                            </SelectItem>
                            <SelectItem
                              value="engineering"
                              className="text-[10px] font-bold uppercase tracking-widest"
                            >
                              Engineering
                            </SelectItem>
                            <SelectItem
                              value="hr"
                              className="text-[10px] font-bold uppercase tracking-widest"
                            >
                              Human Resources
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/80 ml-0.5">
                        Geo Location
                      </Label>
                      <div className="relative">
                        <MapPin className="w-3 h-3 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground opacity-50" />
                        <Input
                          placeholder="City, State, Country"
                          className="h-9 pl-8 bg-muted/10 border-border/50 rounded-lg text-xs font-bold focus:border-primary transition-all shadow-none"
                          defaultValue={location}
                          onBlur={(e) =>
                            updateSearchParam("location", e.target.value)
                          }
                          onKeyDown={(e) =>
                            e.key === "Enter" &&
                            updateSearchParam("location", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* 2. Company Data */}
                <div className="space-y-4">
                  <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60 flex items-center gap-2 border-b border-border/40 pb-2">
                    <Building className="w-3 h-3" /> Firmographics
                  </h4>
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/80 ml-0.5">
                        Org Name / Domain
                      </Label>
                      <Input
                        placeholder="Exact match or domain"
                        className="h-9 bg-muted/10 border-border/50 rounded-lg text-xs font-bold focus:border-primary transition-all shadow-none"
                        defaultValue={companyName}
                        onBlur={(e) =>
                          updateSearchParam("companyName", e.target.value)
                        }
                        onKeyDown={(e) =>
                          e.key === "Enter" &&
                          updateSearchParam("companyName", e.target.value)
                        }
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/80 ml-0.5">
                          Industry
                        </Label>
                        <Select
                          value={companyIndustry}
                          onValueChange={(val) =>
                            updateSearchParam("companyIndustry", val)
                          }
                        >
                          <SelectTrigger className="h-9 text-[10px] font-bold bg-muted/10 border-border/50 rounded-lg">
                            <SelectValue placeholder="Any Industry" />
                          </SelectTrigger>
                          <SelectContent className="rounded-xl border-border">
                            <SelectItem
                              value="any"
                              className="text-[10px] font-bold"
                            >
                              Any Industry
                            </SelectItem>
                            <SelectItem
                              value="saas"
                              className="text-[10px] font-bold"
                            >
                              Software / SaaS
                            </SelectItem>
                            <SelectItem
                              value="fintech"
                              className="text-[10px] font-bold"
                            >
                              Fintech
                            </SelectItem>
                            <SelectItem
                              value="healthcare"
                              className="text-[10px] font-bold"
                            >
                              Healthcare
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/80 ml-0.5">
                          Employee Size
                        </Label>
                        <Select
                          value={companySize}
                          onValueChange={(val) =>
                            updateSearchParam("companySize", val)
                          }
                        >
                          <SelectTrigger className="h-9 text-[10px] font-bold bg-muted/10 border-border/50 rounded-lg">
                            <SelectValue placeholder="Any Size" />
                          </SelectTrigger>
                          <SelectContent className="rounded-xl border-border">
                            <SelectItem
                              value="any"
                              className="text-[10px] font-bold"
                            >
                              Any Size
                            </SelectItem>
                            <SelectItem
                              value="1-10"
                              className="text-[10px] font-bold"
                            >
                              1 - 10
                            </SelectItem>
                            <SelectItem
                              value="11-50"
                              className="text-[10px] font-bold"
                            >
                              11 - 50
                            </SelectItem>
                            <SelectItem
                              value="51-200"
                              className="text-[10px] font-bold"
                            >
                              51 - 200
                            </SelectItem>
                            <SelectItem
                              value="201-500"
                              className="text-[10px] font-bold"
                            >
                              201 - 500
                            </SelectItem>
                            <SelectItem
                              value="501+"
                              className="text-[10px] font-bold"
                            >
                              501+
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 3. Signals & Intelligence */}
                <div className="space-y-4">
                  <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60 flex items-center gap-2 border-b border-border/40 pb-2">
                    <Activity className="w-3 h-3" /> Signals & Reach
                  </h4>
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/80 ml-0.5">
                        Verification & Intent
                      </Label>
                      <div className="grid grid-cols-1 gap-2">
                        <Select
                          value={verificationStatus}
                          onValueChange={(val) =>
                            updateSearchParam("verificationStatus", val)
                          }
                        >
                          <SelectTrigger className="h-9 text-[10px] font-bold bg-muted/10 border-border/50 rounded-lg">
                            <SelectValue placeholder="Any Status" />
                          </SelectTrigger>
                          <SelectContent className="rounded-xl border-border">
                            <SelectItem
                              value="any"
                              className="text-[10px] font-bold uppercase tracking-widest"
                            >
                              Any Status
                            </SelectItem>
                            <SelectItem
                              value="VERIFIED"
                              className="text-[10px] font-bold uppercase tracking-widest text-emerald-500"
                            >
                              Verified Precision
                            </SelectItem>
                            <SelectItem
                              value="RISKY"
                              className="text-[10px] font-bold uppercase tracking-widest text-amber-500"
                            >
                              Risky / Catch-All
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <CellTooltip tooltip="Filters by behavioral buying interest and predictive signals">
                          <div className="w-full">
                            <Select
                              value={minIntent}
                              onValueChange={(val) =>
                                updateSearchParam("minIntent", val)
                              }
                            >
                              <SelectTrigger className="h-9 text-[10px] font-bold bg-muted/10 border-border/50 rounded-lg">
                                <SelectValue placeholder="Intent Band" />
                              </SelectTrigger>
                              <SelectContent className="rounded-xl border-border">
                                <SelectItem
                                  value="any"
                                  className="text-[10px] font-bold uppercase tracking-widest"
                                >
                                  Any Intent
                                </SelectItem>
                                <SelectItem
                                  value="80"
                                  className="text-[10px] font-bold uppercase tracking-widest text-rose-500"
                                >
                                  {t("contacts.global.labels.hotIntent")}
                                </SelectItem>
                                <SelectItem
                                  value="50"
                                  className="text-[10px] font-bold uppercase tracking-widest text-amber-500"
                                >
                                  {t("contacts.global.labels.warm")}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </CellTooltip>
                      </div>
                    </div>

                    <div className="space-y-2 pt-1">
                      <Label className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/80 ml-0.5">
                        {t("contacts.global.labels.reachabilityAdoption")}
                      </Label>
                      <div className="grid grid-cols-1 gap-2">
                        <div
                          className="flex items-center justify-between px-2 py-1.5 bg-muted/5 border border-border/30 rounded-lg hover:border-primary/30 transition-all cursor-pointer group"
                          onClick={() =>
                            updateSearchParam(
                              "excludeAdopted",
                              !excludeAdopted ? "true" : "",
                            )
                          }
                        >
                          <span className="text-[10px] font-bold text-muted-foreground group-hover:text-primary transition-colors">
                            {t("contacts.global.labels.excludeSaved")}
                          </span>
                          <input
                            type="checkbox"
                            checked={excludeAdopted}
                            readOnly
                            className="rounded border-border w-3.5 h-3.5 text-primary accent-primary"
                          />
                        </div>
                        <div
                          className="flex items-center justify-between px-2 py-1.5 bg-muted/5 border border-border/30 rounded-lg hover:border-primary/30 transition-all cursor-pointer group"
                          onClick={() =>
                            updateSearchParam(
                              "hasEmail",
                              !hasEmail ? "true" : "",
                            )
                          }
                        >
                          <span className="text-[10px] font-bold text-muted-foreground group-hover:text-primary transition-colors">
                            {t("contacts.global.labels.hasEmail")}
                          </span>
                          <input
                            type="checkbox"
                            checked={hasEmail}
                            readOnly
                            className="rounded border-border w-3.5 h-3.5 text-primary accent-primary"
                          />
                        </div>
                        <div
                          className="flex items-center justify-between px-2 py-1.5 bg-muted/5 border border-border/30 rounded-lg hover:border-primary/30 transition-all cursor-pointer group"
                          onClick={() =>
                            updateSearchParam(
                              "hasPhone",
                              !hasPhone ? "true" : "",
                            )
                          }
                        >
                          <span className="text-[10px] font-bold text-muted-foreground group-hover:text-primary transition-colors">
                            Requires Phone
                          </span>
                          <input
                            type="checkbox"
                            checked={hasPhone}
                            readOnly
                            className="rounded border-border w-3.5 h-3.5 text-primary accent-primary"
                          />
                        </div>
                        <div
                          className="flex items-center justify-between px-2 py-1.5 bg-muted/5 border border-border/30 rounded-lg hover:border-primary/30 transition-all cursor-pointer group"
                          onClick={() =>
                            updateSearchParam(
                              "hasLinkedin",
                              !hasLinkedin ? "true" : "",
                            )
                          }
                        >
                          <span className="text-[10px] font-bold text-muted-foreground group-hover:text-primary transition-colors">
                            Requires LinkedIn
                          </span>
                          <input
                            type="checkbox"
                            checked={hasLinkedin}
                            readOnly
                            className="rounded border-border w-3.5 h-3.5 text-primary accent-primary"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 4. Staleness & Engagement */}
                <div className="space-y-4">
                  <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60 flex items-center gap-2 border-b border-border/40 pb-2">
                    <Clock className="w-3 h-3" /> Data Health & CRM
                  </h4>
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 gap-2">
                      <Select
                        value={searchParams.get("engagementStage") || "any"}
                        onValueChange={(val) =>
                          updateSearchParam("engagementStage", val)
                        }
                      >
                        <SelectTrigger className="h-9 text-[10px] font-bold bg-muted/10 border-border/50 rounded-lg">
                          <SelectValue placeholder="Engagement Stage" />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl border-border">
                          <SelectItem
                            value="any"
                            className="text-[10px] font-bold"
                          >
                            Unengaged
                          </SelectItem>
                          <SelectItem
                            value="active"
                            className="text-[10px] font-bold"
                          >
                            In Active Sequence
                          </SelectItem>
                          <SelectItem
                            value="replied"
                            className="text-[10px] font-bold"
                          >
                            Replied / Interested
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <Select
                        value={searchParams.get("staleness") || "any"}
                        onValueChange={(val) =>
                          updateSearchParam("staleness", val)
                        }
                      >
                        <SelectTrigger className="h-9 text-[10px] font-bold bg-muted/10 border-border/50 rounded-lg">
                          <SelectValue placeholder="Data Quality / Freshness" />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl border-border">
                          <SelectItem
                            value="any"
                            className="text-[10px] font-bold"
                          >
                            Any Freshness
                          </SelectItem>
                          <SelectItem
                            value="recent"
                            className="text-[10px] font-bold"
                          >
                            Updated (Last 30d)
                          </SelectItem>
                          <SelectItem
                            value="historical"
                            className="text-[10px] font-bold"
                          >
                            Historical Data
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* 4. Tags & Metadata */}
                <div className="space-y-4">
                  <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60 flex items-center gap-2 border-b border-border/40 pb-2">
                    <TagIcon className="w-3 h-3" /> Labels & Tags
                  </h4>
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/80 ml-0.5">
                        Filter by CRM Tags
                      </Label>
                      <div className="relative">
                        <TagIcon className="w-3 h-3 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground opacity-50" />
                        <Input
                          placeholder="e.g. Q4_Leads, EMEA"
                          className="h-9 pl-8 bg-muted/10 border-border/50 rounded-lg text-xs font-bold focus:border-primary transition-all shadow-none"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 flex flex-col min-h-0 overflow-hidden relative min-w-0 bg-muted/5">
            {/* Apollo-style Top Control Bar */}
            <div className="h-12 flex-none sticky top-0 z-10 border-b border-border/40 bg-background/95 backdrop-blur-md flex items-center justify-between px-4">
              <div className="flex items-center gap-4">
                <div className="relative group">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/50 w-3.5 h-3.5 group-focus-within:text-primary transition-colors" />
                  <input
                    placeholder="Search results..."
                    className="h-8 w-64 bg-transparent border-none rounded-lg pl-9 pr-3 text-[11px] font-black uppercase tracking-widest focus:ring-1 focus:ring-primary/20 transition-all placeholder:text-muted-foreground/30 text-foreground"
                    defaultValue={search}
                    onBlur={(e) => updateSearchParam("q", e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && updateSearchParam("q", e.target.value)}
                  />
                </div>
                <div className="w-px h-4 bg-border/60 mx-1" />
                <SelectFilter
                  label="Sort"
                  value={sortField}
                  onChange={(val) => updateSearchParam("sortField", val)}
                  options={[
                    { value: "confidenceScore", label: t("contacts.global.labels.matchIntegrity") },
                    { value: "createdAt", label: "Recently Added" },
                    { value: "lastName", label: "Alphabetical" },
                    { value: "intentScore", label: t("contacts.global.labels.hotIntent") },
                  ]}
                />
              </div>
              <div className="flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="h-8 px-3 text-[10px] font-black uppercase tracking-widest text-muted-foreground flex items-center gap-1.5 rounded-lg border border-border/40 hover:bg-muted transition-all">
                      <Columns className="w-3.5 h-3.5" /> Columns
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-48 rounded-xl shadow-2xl border border-border p-1 bg-card"
                  >
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-widest font-black text-muted-foreground p-3">
                      Toggle Columns
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-border/60" />
                    {allColumns.map((col) => (
                      <DropdownMenuCheckboxItem
                        key={col.id}
                        className="font-bold cursor-pointer text-[10px] uppercase tracking-widest rounded-lg py-2"
                        checked={visibleColumns.includes(col.id)}
                        onCheckedChange={() => toggleColumn(col.id)}
                        onSelect={(e) => e.preventDefault()}
                      >
                        {col.header}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                {searchParams.toString() && (
                  <button
                    onClick={clearAllFilters}
                    className="h-8 px-3 text-[10px] font-black uppercase tracking-widest text-muted-foreground hover:bg-destructive/5 hover:text-destructive flex items-center gap-1.5 rounded-lg border border-border/40 transition-all"
                  >
                    <XCircle className="w-3.5 h-3.5" /> Clear
                  </button>
                )}

                <button
                  onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                  className="h-8 px-3 rounded-lg hover:bg-muted text-muted-foreground transition-all flex items-center gap-2 border border-border/40"
                >
                  <Filter className="w-3.5 h-3.5" />
                  <span className="text-[10px] font-black uppercase tracking-widest">
                    {isSidebarOpen ? "Hide Filters" : "Show Filters"}
                  </span>
                </button>
              </div>
            </div>

            {/* Active Facet Chips */}
            <div className="px-4 py-2 flex-none sticky top-12 z-[9] border-b border-border/20 bg-muted/95 backdrop-blur-md">
              <div className="flex flex-wrap items-center gap-1.5 min-h-[1.5rem]">
                {(() => {
                  const chips = [];
                  if (verificationStatus !== "any")
                    chips.push({
                      k: "verificationStatus",
                      l: `${verificationStatus}`,
                    });
                  if (titleFilter)
                    chips.push({
                      k: "title",
                      l: `${titleFilter.startsWith("exact:")
                          ? titleFilter.slice(6)
                          : titleFilter
                            .split("_")
                            .map(
                              (w) =>
                                w.charAt(0).toUpperCase() +
                                w.slice(1).toLowerCase(),
                            )
                            .join(" ")
                        }`,
                    });
                  if (seniority !== "any")
                    chips.push({
                      k: "seniority",
                      l: `${seniority.charAt(0).toUpperCase() + seniority.slice(1)}`,
                    });
                  if (func !== "any")
                    chips.push({ k: "function", l: `${func.toUpperCase()}` });
                  if (includeRelatedRoles)
                    chips.push({ k: "relatedRoles", l: "Related" });
                  if (location)
                    chips.push({
                      k: "location",
                      l: `${location.split(",")[0]}`,
                    });
                  if (companyName)
                    chips.push({ k: "companyName", l: `${companyName}` });
                  if (companyIndustry !== "any")
                    chips.push({
                      k: "companyIndustry",
                      l: `${companyIndustry.toUpperCase()}`,
                    });
                  if (companySize !== "any")
                    chips.push({ k: "companySize", l: `${companySize}` });
                  if (minIntent !== "any")
                    chips.push({
                      k: "minIntent",
                      l:
                        minIntent === "80"
                          ? t("contacts.global.labels.hotIntent")
                          : t("contacts.global.labels.warm"),
                    });
                  if (hasEmail)
                    chips.push({ k: "hasEmail", l: t("contacts.global.labels.hasEmail") });
                  if (hasLinkedin)
                    chips.push({ k: "hasLinkedin", l: t("contacts.global.labels.hasLink") });
                  if (excludeAdopted)
                    chips.push({ k: "excludeAdopted", l: t("contacts.global.labels.notSaved") });

                  return chips.map((c) => (
                    <div
                      key={c.k}
                      className="flex items-center gap-1 pl-2 pr-1 py-0.5 bg-primary/5 border border-primary/20 rounded-md text-[9px] font-black uppercase tracking-widest text-primary/80 animate-in fade-in zoom-in duration-200"
                    >
                      {c.l}{" "}
                      <X
                        onClick={() => updateSearchParam(c.k, "")}
                        className="w-2.5 h-2.5 cursor-pointer ml-0.5 opacity-40 hover:opacity-100 hover:scale-110 transition-all"
                      />
                    </div>
                  ));
                })()}
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-hidden relative">
              <DataTable
                variant="workspace"
                columns={allColumns.filter((c) =>
                  visibleColumns.includes(c.id),
                )}
                data={loading ? [] : people}
                loading={loading}
                onRowClick={handleRowClick}
                pagination={{
                  page,
                  limit,
                  totalRecords,
                  totalPages,
                }}
                onPageChange={setPage}
                onLimitChange={(newLimit) => {
                  setLimit(newLimit);
                  setPage(1);
                }}
                selectable={{
                  selectedIds: selectedPeople,
                  onSelectionChange: setSelectedPeople,
                  rowId: (row) => row._id,
                  advanced: true,
                  onAdvancedSelect: handleAdvancedSelection,
                }}
                bulkActions={
                  selectedPeople.length > 0
                    ? [
                      {
                        label: `Unlock ${selectedPeople.length} Emails`,
                        icon: Key,
                        variant: "primary",
                        onClick: openBulkUnlockModal,
                      },
                      {
                        label: t("discovery.actions.addToList"),
                        icon: Plus,
                        variant: "outline",
                        onClick: openAddToListModal,
                      },
                      {
                        label: t("discovery.actions.addToGroup"),
                        icon: Users,
                        variant: "outline",
                        onClick: () => {
                          fetchGroups();
                          setIsAddToGroupModalOpen(true);
                        },
                      },
                      {
                        label: t("contacts.global.actions.copyEmails"),
                        icon: Mail,
                        variant: "outline",
                        onClick: handleCopyEmails,
                      },
                      {
                        label: t("contacts.global.actions.copyLinkedin"),
                        icon: Globe,
                        variant: "outline",
                        onClick: handleCopyLinkedin,
                      },
                      {
                        label: "Add to Sequence",
                        icon: Download,
                        variant: "outline",
                        onClick: () => setIsSequenceModalOpen(true),
                      },
                      {
                        label: "Assign Owner",
                        icon: User,
                        variant: "outline",
                        onClick: () => setIsOwnerModalOpen(true),
                      },
                      {
                        label: "Tag",
                        icon: TagIcon,
                        variant: "outline",
                        onClick: () => setIsTagModalOpen(true),
                      },
                      {
                        label: "Export CSV",
                        icon: Database,
                        variant: "outline",
                        onClick: handleExportCSV,
                      },
                    ]
                    : null
                }
                emptyState={
                  <div className="flex flex-col items-center justify-center p-10 text-center bg-transparent rounded-[1.5rem] border border-border/30 m-2">
                    <div className="relative mb-6">
                      <div className="w-16 h-16 rounded-2xl bg-muted/30 flex items-center justify-center ring-4 ring-muted/10 z-10 relative">
                        <Search className="w-7 h-7 text-muted-foreground/50" />
                      </div>
                    </div>
                    <h3 className="text-lg font-black tracking-tight text-foreground/80 mb-1">
                      No Matching Contacts
                    </h3>

                    {titleFilter && !includeRelatedRoles ? (
                      <div className="max-w-md mx-auto mb-6 bg-amber-500/5 border border-amber-500/15 p-3 rounded-lg text-left">
                        <p className="text-xs text-foreground/70 font-medium flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 text-amber-500/70 flex-shrink-0 mt-0.5" />
                          <span>
                            0 exact matches for{" "}
                            <strong className="font-black">
                              "
                              {titleFilter.startsWith("exact:")
                                ? titleFilter.split("exact:")[1]
                                : titleFilter}
                              "
                            </strong>
                            . Try{" "}
                            <strong
                              className="text-amber-600 dark:text-amber-400 cursor-pointer hover:underline"
                              onClick={() =>
                                updateSearchParam("relatedRoles", "true")
                              }
                            >
                              Include related roles
                            </strong>
                            .
                          </span>
                        </p>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground/60 max-w-xs mb-6 font-medium">
                        Your filters returned 0 records. Try broadening the
                        search scope.
                      </p>
                    )}

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearAllFilters}
                      className="h-8 px-4 rounded-lg font-bold uppercase tracking-widest text-[10px] border-border/40 text-muted-foreground hover:text-foreground"
                    >
                      Clear Filters
                    </Button>
                  </div>
                }
              />
            </div>
          </div>
        </div>

        <Sheet open={isSaveModalOpen} onOpenChange={setIsSaveModalOpen}>
          <SheetContent side="right" className="sm:max-w-md rounded-2xl border-none shadow-2xl">
            <SheetHeader className="space-y-3">
              <SheetTitle className="text-2xl font-extrabold tracking-tight text-foreground flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/20">
                  <Bookmark className="w-6 h-6 text-primary" />
                </div>
                Save Workspace View
              </SheetTitle>
              <p className="text-sm text-muted-foreground font-medium leading-relaxed">
                Serialize your current filters, search query, and table state
                into a persistent segment. Share the segment URL with your team.
              </p>
            </SheetHeader>
            <div className="py-4 space-y-6">
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/70 ml-1">
                  View Display Name
                </Label>
                <Input
                  autoFocus
                  value={newViewName}
                  onChange={(e) => setNewViewName(e.target.value)}
                  placeholder="e.g. Enterprise HR Leaders - USA"
                  className="h-12 bg-muted/20 border-2 border-border/50 rounded-2xl text-sm font-bold focus:border-primary transition-all shadow-sm"
                />
                <p className="text-[9px] font-bold text-muted-foreground/50 ml-1 uppercase tracking-widest">
                  Supports alphanumeric and special characters
                </p>
              </div>
            </div>
            <SheetFooter className="gap-3 pt-4">
              <Button
                variant="ghost"
                className="flex-1 rounded-xl font-bold uppercase text-[10px] tracking-widest h-12"
                onClick={() => setIsSaveModalOpen(false)}
              >
                Discard
              </Button>
              <Button
                className="flex-1 rounded-xl font-black uppercase text-[10px] tracking-widest h-12 shadow-lg shadow-primary/20"
                onClick={handleSaveView}
                disabled={!newViewName.trim()}
              >
                Save Definition
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        <Sheet open={isUpdateModalOpen} onOpenChange={setIsUpdateModalOpen}>
          <SheetContent side="right" className="sm:max-w-md rounded-2xl border-none shadow-2xl">
            <SheetHeader className="space-y-3">
              <SheetTitle className="text-2xl font-extrabold tracking-tight text-foreground flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center border border-amber-500/20">
                  <AlertTriangle className="w-6 h-6 text-amber-500" />
                </div>
                Update Segment Definition
              </SheetTitle>
              <p className="text-sm text-muted-foreground font-medium leading-relaxed">
                You are modifying the core filters for{" "}
                <strong className="font-extrabold text-foreground">
                  "{activeView?.name}"
                </strong>
                . This will affect all operators using this view.
              </p>
            </SheetHeader>
            <SheetFooter className="gap-3 pt-4">
              <Button
                variant="ghost"
                className="flex-1 rounded-xl font-bold uppercase text-[10px] tracking-widest h-12"
                onClick={() => setIsUpdateModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 rounded-xl font-black uppercase text-[10px] tracking-widest h-12 bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-500/20"
                onClick={handleUpdateView}
              >
                Confirm Update
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        {/* Bulk Actions Modals */}
        <Sheet
          open={isAddToListModalOpen}
          onOpenChange={setIsAddToListModalOpen}
        >
          <SheetContent side="right" className="sm:max-w-lg rounded-2xl border-none shadow-2xl">
            <SheetHeader className="space-y-3">
              <SheetTitle className="text-2xl font-extrabold tracking-tight text-foreground flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/20">
                  <Plus className="w-6 h-6 text-primary" />
                </div>
                {t("discovery.modals.addToList.title")}
              </SheetTitle>
              <p className="text-sm text-muted-foreground font-medium leading-relaxed">
                {t("discovery.modals.addToList.description")}
              </p>
            </SheetHeader>
            <div className="py-4 space-y-6">
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/70 ml-1">
                  {t("discovery.modals.addToList.listName")}
                </Label>
                <Input
                  autoFocus
                  value={listNameInput}
                  onChange={(e) => setListNameInput(e.target.value)}
                  placeholder={t("discovery.modals.addToList.listNamePlaceholder")}
                  className="h-12 bg-muted/20 border-2 border-border/50 rounded-2xl text-sm font-bold focus:border-primary transition-all shadow-sm"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/70 ml-1">
                  {t("discovery.common.visibility")}
                </Label>
                <Select
                  value={listVisibility}
                  onValueChange={setListVisibility}
                >
                  <SelectTrigger className="w-full rounded-2xl h-12 border-2 bg-muted/20 border-border/50 shadow-sm text-sm font-bold">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-2xl border-border/50 shadow-2xl">
                    <SelectItem value="PRIVATE" className="text-xs font-bold py-3">
                      {t("discovery.visibility.private")}
                    </SelectItem>
                    <SelectItem
                      value="WORKSPACE_SHARED"
                      className="text-xs font-bold py-3"
                    >
                      {t("discovery.visibility.shared")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/70 ml-1">
                  {t("contacts.global.labels.existingLists")}
                </Label>
                <div className="flex flex-wrap gap-2">
                  {isProspectListsLoading ? (
                    <span className="text-xs text-muted-foreground">
                      {t("common.loading")}
                    </span>
                  ) : prospectLists.length > 0 ? (
                    prospectLists.slice(0, 8).map((list) => (
                      <button
                        key={list._id}
                        type="button"
                        onClick={() => {
                          setListNameInput(list.name);
                          setListVisibility(
                            list.visibility || "PRIVATE",
                          );
                        }}
                        className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${listNameInput.trim().toLowerCase() ===
                            list.name?.toLowerCase()
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border/60 bg-muted/30 text-foreground hover:bg-muted"
                          }`}
                      >
                        {list.name}
                      </button>
                    ))
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      {t("contacts.global.empty.noLists")}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <SheetFooter className="gap-3 pt-4">
              <Button
                variant="ghost"
                className="flex-1 rounded-xl font-bold uppercase text-[10px] tracking-widest h-12"
                onClick={() => setIsAddToListModalOpen(false)}
              >
                {t("actions.cancel")}
              </Button>
              <Button
                className="flex-1 rounded-xl font-black uppercase text-[10px] tracking-widest h-12 shadow-lg shadow-primary/20"
                onClick={handleAddToList}
                disabled={!listNameInput.trim() || listActionLoading}
              >
                {listActionLoading
                  ? t("common.loading")
                  : t("contacts.global.actions.addToList")}
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        <Sheet
          open={isAddToGroupModalOpen}
          onOpenChange={setIsAddToGroupModalOpen}
        >
          <SheetContent side="right" className="sm:max-w-lg rounded-2xl border-none shadow-2xl">
            <SheetHeader className="space-y-3">
              <SheetTitle className="text-2xl font-extrabold tracking-tight text-foreground flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/20">
                  <Users className="w-6 h-6 text-primary" />
                </div>
                {t("discovery.modals.addToGroup.title")}
              </SheetTitle>
              <p className="text-sm text-muted-foreground font-medium leading-relaxed">
                {t("discovery.modals.addToGroup.description")}
              </p>
            </SheetHeader>
            <div className="py-4 space-y-6">
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/70 ml-1">
                  {t("discovery.modals.addToGroup.groupName")}
                </Label>
                <Select
                  value={selectedGroupId}
                  onValueChange={setSelectedGroupId}
                >
                  <SelectTrigger className="w-full rounded-2xl h-12 border-2 bg-muted/20 border-border/50 shadow-sm text-sm font-bold">
                    <SelectValue placeholder={t("discovery.modals.addToGroup.groupPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent className="rounded-2xl border-border/50 shadow-2xl">
                    {isGroupsLoading ? (
                      <div className="p-4 text-center">
                        <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                      </div>
                    ) : groups.length > 0 ? (
                      groups.map((group) => (
                        <SelectItem key={group._id} value={group._id} className="text-xs font-bold py-3">
                          {group.name}
                        </SelectItem>
                      ))
                    ) : (
                      <div className="p-4 text-center text-xs text-muted-foreground">
                        No static groups found
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <SheetFooter className="gap-3 pt-4">
              <Button
                variant="ghost"
                className="flex-1 rounded-xl font-bold uppercase text-[10px] tracking-widest h-12"
                onClick={() => setIsAddToGroupModalOpen(false)}
              >
                {t("actions.cancel")}
              </Button>
              <Button
                className="flex-1 rounded-xl font-black uppercase text-[10px] tracking-widest h-12 shadow-lg shadow-primary/20"
                onClick={handleAddToGroup}
                disabled={!selectedGroupId || groupActionLoading}
              >
                {groupActionLoading
                  ? t("common.loading")
                  : t("discovery.actions.addToGroup")}
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        <Sheet
          open={isSequenceModalOpen}
          onOpenChange={setIsSequenceModalOpen}
        >
          <SheetContent side="right" className="sm:max-w-md rounded-2xl border-none shadow-2xl">
            <SheetHeader className="space-y-3">
              <SheetTitle className="text-2xl font-extrabold tracking-tight text-foreground flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/20">
                  <Download className="w-6 h-6 text-primary" />
                </div>
                Enrollment Routine
              </SheetTitle>
              <p className="text-sm text-muted-foreground font-medium leading-relaxed">
                Enrolling{" "}
                <strong className="font-extrabold text-foreground">
                  {selectedPeople.length} contacts
                </strong>
                . Target records will be adopted into your CRM before sequence
                execution.
              </p>
            </SheetHeader>
            <div className="py-4 space-y-6">
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/70 ml-1">
                  Select Output Sequence
                </Label>
                <Select
                  value={selectedSequenceId}
                  onValueChange={setSelectedSequenceId}
                >
                  <SelectTrigger className="w-full rounded-2xl h-12 border-2 bg-muted/20 border-border/50 shadow-sm text-sm font-bold">
                    <SelectValue placeholder="Choose an active sequence..." />
                  </SelectTrigger>
                  <SelectContent className="rounded-2xl border-border/50 shadow-2xl">
                    {sequences.map((seq) => (
                      <SelectItem
                        key={seq._id}
                        value={seq._id}
                        className="text-xs font-bold py-3 uppercase tracking-widest"
                      >
                        {seq.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <SheetFooter className="gap-3 pt-4">
              <Button
                variant="ghost"
                className="flex-1 rounded-xl font-bold uppercase text-[10px] tracking-widest h-12"
                onClick={() => setIsSequenceModalOpen(false)}
              >
                Discard
              </Button>
              <Button
                className="flex-1 rounded-xl font-black uppercase text-[10px] tracking-widest h-12 shadow-lg shadow-primary/20"
                onClick={handleBulkSequence}
                disabled={!selectedSequenceId || isBulkLoading}
              >
                {isBulkLoading ? "Processing..." : "Run Enrollment"}
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        <Sheet open={isOwnerModalOpen} onOpenChange={setIsOwnerModalOpen}>
          <SheetContent side="right" className="sm:max-w-md rounded-2xl border-none shadow-2xl">
            <SheetHeader className="space-y-3">
              <SheetTitle className="text-2xl font-extrabold tracking-tight text-foreground flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/20">
                  <User className="w-6 h-6 text-primary" />
                </div>
                Ownership Assignment
              </SheetTitle>
              <p className="text-sm text-muted-foreground font-medium leading-relaxed">
                Assigning{" "}
                <strong className="font-extrabold text-foreground">
                  {selectedPeople.length} contacts
                </strong>
                . Selected operator will become the single source of truth for
                these records.
              </p>
            </SheetHeader>
            <div className="py-4 space-y-6">
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/70 ml-1">
                  Workspace Operator
                </Label>
                <Select
                  value={selectedOwnerId}
                  onValueChange={setSelectedOwnerId}
                >
                  <SelectTrigger className="w-full rounded-2xl h-12 border-2 bg-muted/20 border-border/50 shadow-sm text-sm font-bold">
                    <SelectValue placeholder="Assign a CRM owner..." />
                  </SelectTrigger>
                  <SelectContent className="rounded-2xl border-border/50 shadow-2xl">
                    {users.map((u) => (
                      <SelectItem
                        key={u.userId}
                        value={u.userId}
                        className="text-xs font-bold py-3 uppercase tracking-widest"
                      >
                        {u.name || u.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <SheetFooter className="gap-3 pt-4">
              <Button
                variant="ghost"
                className="flex-1 rounded-xl font-bold uppercase text-[10px] tracking-widest h-12"
                onClick={() => setIsOwnerModalOpen(false)}
              >
                Discard
              </Button>
              <Button
                className="flex-1 rounded-xl font-black uppercase text-[10px] tracking-widest h-12 shadow-lg shadow-primary/20"
                onClick={handleBulkOwner}
                disabled={!selectedOwnerId || isBulkLoading}
              >
                {isBulkLoading ? "Processing..." : "Assign Owner"}
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        <Sheet open={isTagModalOpen} onOpenChange={setIsTagModalOpen}>
          <SheetContent side="right" className="sm:max-w-md rounded-2xl border-none shadow-2xl">
            <SheetHeader className="space-y-3">
              <SheetTitle className="text-2xl font-extrabold tracking-tight text-foreground flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/20">
                  <TagIcon className="w-6 h-6 text-primary" />
                </div>
                Bulk Tagging
              </SheetTitle>
              <p className="text-sm text-muted-foreground font-medium leading-relaxed">
                Applying classification labels to{" "}
                <strong className="font-extrabold text-foreground">
                  {selectedPeople.length} contacts
                </strong>
                . Supported: comma-separated strings.
              </p>
            </SheetHeader>
            <div className="py-4 space-y-6">
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/70 ml-1">
                  Semantic Labels
                </Label>
                <Input
                  autoFocus
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="e.g. enterprise_lead, urgent_q4"
                  className="h-12 bg-muted/20 border-2 border-border/50 rounded-2xl text-sm font-bold focus:border-primary transition-all shadow-sm"
                />
              </div>
            </div>
            <SheetFooter className="gap-3 pt-4">
              <Button
                variant="ghost"
                className="flex-1 rounded-xl font-bold uppercase text-[10px] tracking-widest h-12"
                onClick={() => setIsTagModalOpen(false)}
              >
                Discard
              </Button>
              <Button
                className="flex-1 rounded-xl font-black uppercase text-[10px] tracking-widest h-12 shadow-lg shadow-primary/20"
                onClick={handleBulkTag}
                disabled={!tagInput.trim() || isBulkLoading}
              >
                {isBulkLoading ? "Processing..." : "Apply Labels"}
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        {/* Unlock Confirmation Modal */}
        <Sheet
          open={unlockModal.isOpen}
          onOpenChange={(open) =>
            !unlockModalIsLoading &&
            setUnlockModal((prev) => ({ ...prev, isOpen: open }))
          }
        >
          <SheetContent side="right" className="sm:max-w-md rounded-2xl border-border/50 shadow-2xl p-0 overflow-hidden bg-card">
            <div className="px-6 py-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
                  <Key className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <h3 className="text-lg font-black tracking-tight text-foreground">
                    Unlock Contact Email
                  </h3>
                  <p className="text-sm font-medium text-muted-foreground mt-0.5 opacity-80">
                    {unlockModal.isBulk
                      ? `You are about to unlock ${selectedPeople.length} contacts.`
                      : `You are about to unlock a verified work email.`}
                  </p>
                </div>
              </div>

              <div className="bg-muted/10 border border-amber-500/20 rounded-xl p-4 text-sm font-medium text-foreground/80 leading-relaxed shadow-sm">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                  <div>
                    <span className="font-bold text-foreground">
                      Important:
                    </span>
                    {unlockModal.isBulk
                      ? ` This action will deduct up to ${selectedPeople.length * unlockEmailCreditCost} credits from your workspace balance.`
                      : ` This action will deduct ${unlockEmailCreditCost} credits from your workspace balance.`}
                    <br />
                    Remaining email credits:{" "}
                    <strong className="font-extrabold text-foreground">
                      {remainingEmailCreditsLabel}
                    </strong>
                    <br />
                    <br />
                    Once unlocked, the contact will be permanently saved to your
                    workspace and future access will not consume credits.
                  </div>
                </div>
              </div>
            </div>
            <SheetFooter className="px-6 py-4 bg-muted/20 border-t border-border/40 gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() =>
                  setUnlockModal((prev) => ({ ...prev, isOpen: false }))
                }
                disabled={unlockModalIsLoading}
                className="font-bold rounded-xl h-10 border-border/60 hover:bg-muted"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmUnlock}
                disabled={unlockModalIsLoading}
                className="bg-primary hover:bg-primary/90 text-white font-black rounded-xl h-10 shadow-lg shadow-primary/25 min-w-[140px]"
              >
                {unlockModalIsLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />{" "}
                    Unlocking...
                  </>
                ) : (
                  "Confirm & Unlock"
                )}
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        {/* --- Detail Drawer (Contact Inspection) --- */}
        <Sheet
          open={!!activeContactId}
          onOpenChange={(open) => {
            // Ignore dismissal if any secondary action modal is open
            if (!open && !isAnyModalOpen) {
              handleCloseDrawer();
            }
          }}
        >
          <SheetContent
            onPointerDownOutside={(e) => {
              // Prevent closure if a modal is active
              if (isAnyModalOpen) e.preventDefault();
            }}
            onEscapeKeyDown={(e) => {
              // Prevent closure if a modal is active
              if (isAnyModalOpen) e.preventDefault();
            }}
            className="w-[90vw] sm:max-w-md xl:max-w-[480px] p-0 flex flex-col gap-0 border-l border-border shadow-2xl z-50"
          >
            {!displayContact && isDrawerHydrating ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8 bg-card/50 backdrop-blur-sm">
                <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
                <div className="text-[10px] uppercase font-black tracking-widest text-muted-foreground/60 animate-pulse">
                  Hydrating Intelligence
                </div>
              </div>
            ) : drawerError ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-card h-full">
                <div className="w-16 h-16 rounded-2xl bg-destructive/10 flex items-center justify-center mb-6 ring-4 ring-destructive/5">
                  <AlertCircle className="w-8 h-8 text-destructive/70" />
                </div>
                <h3 className="text-xl font-black tracking-tight text-foreground mb-2">
                  Contact Unavailable
                </h3>
                <p className="text-sm text-muted-foreground font-medium max-w-[260px] leading-relaxed">
                  The requested contact record could not be loaded or has been
                  obfuscated.
                </p>
                <Button
                  onClick={handleCloseDrawer}
                  variant="outline"
                  className="mt-8 rounded-xl font-black uppercase tracking-widest text-[10px] h-10 px-6 border-border/60 hover:bg-muted shadow-sm transition-all focus:ring-2 focus:ring-primary/20"
                >
                  Acknowledge
                </Button>
              </div>
            ) : displayContact ? (
              <div className="flex-1 flex flex-col h-full overflow-hidden">
                <SheetHeader className="flex-none p-6 md:p-8 pt-10 border-b border-border bg-muted/5 relative space-y-0 text-left">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center font-black text-2xl text-primary border border-primary/20 shadow-sm flex-shrink-0">
                      {displayContact.firstName?.[0]}
                      {displayContact.lastName?.[0]}
                    </div>
                    <div className="flex flex-col pt-1">
                      <SheetTitle className="text-2xl font-extrabold tracking-tight text-foreground leading-tight">
                        {displayContact.firstName} {displayContact.lastName}
                      </SheetTitle>
                      <SheetDescription className="text-sm font-bold text-muted-foreground flex items-center gap-1.5 mt-1.5">
                        <Briefcase className="w-3.5 h-3.5 opacity-60" />{" "}
                        {displayContact.title || "Unknown Title"}
                      </SheetDescription>
                      <div className="flex gap-2 mt-4">
                        {displayContact.globalVerificationStatus ===
                          "VERIFIED" && (
                            <CellTooltip tooltip="Email delivery confirmed by active SMTP handshake">
                              <div>
                                <StatusBadge variant="success">
                                  VERIFIED
                                </StatusBadge>
                              </div>
                            </CellTooltip>
                          )}
                        {displayContact.globalVerificationStatus ===
                          "RISKY" && (
                            <CellTooltip tooltip="Catch-all server or unable to actively ping">
                              <div>
                                <StatusBadge variant="warning">RISKY</StatusBadge>
                              </div>
                            </CellTooltip>
                          )}
                        <CellTooltip tooltip="Estimated match relevance based on CRM engagement data">
                          <div className="px-2.5 py-1 rounded-lg border border-border bg-muted/20 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 cursor-help shadow-sm">
                            <ScoreBadge
                              score={displayContact.confidenceScore || 85}
                            />
                            <span className="opacity-60">Match</span>
                          </div>
                        </CellTooltip>
                      </div>
                    </div>
                  </div>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-8 custom-scrollbar bg-card">
                  <Tabs defaultValue="identity" className="w-full">
                    <TabsList className="grid w-full grid-cols-3 mb-8 bg-muted/20 p-1.5 h-auto rounded-2xl border border-border/50">
                      <TabsTrigger
                        value="identity"
                        className="text-[10px] font-black uppercase tracking-widest py-2.5 rounded-xl transition-all data-[state=active]:bg-card data-[state=active]:shadow-md"
                      >
                        Identity
                      </TabsTrigger>
                      <TabsTrigger
                        value="company"
                        className="text-[10px] font-black uppercase tracking-widest py-2.5 rounded-xl transition-all data-[state=active]:bg-card data-[state=active]:shadow-md"
                      >
                        Company
                      </TabsTrigger>
                      <TabsTrigger
                        value="activity"
                        className="text-[10px] font-black uppercase tracking-widest py-2.5 rounded-xl transition-all data-[state=active]:bg-card data-[state=active]:shadow-md"
                      >
                        Intelligence
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent
                      value="identity"
                      className="space-y-8 mt-0 animate-in fade-in slide-in-from-bottom-2 duration-300"
                    >
                      <section className="space-y-4">
                        <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-primary/70 flex items-center gap-2">
                          <Mail className="w-3.5 h-3.5" /> Reachability Check
                        </h4>
                        <div className="bg-muted/10 border border-border/50 rounded-2xl p-4 space-y-4 shadow-sm">
                          {displayContact.isUnlocked &&
                            displayContact.canonicalEmail ? (
                            <div className="flex items-center justify-between">
                              <div className="flex flex-col">
                                <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mb-1">
                                  Primary Email
                                </span>
                                <div className="flex items-center gap-2 text-sm text-foreground font-bold">
                                  {displayContact.canonicalEmail}
                                </div>
                              </div>
                              <div className="flex flex-col gap-1 items-end">
                                <CellTooltip tooltip="Email revealed via credit unlock">
                                  <div>
                                    <StatusBadge variant="success">
                                      UNLOCKED
                                    </StatusBadge>
                                  </div>
                                </CellTooltip>
                                <CellTooltip tooltip="Contact automatically added to workspace CRM">
                                  <div>
                                    <StatusBadge variant="info">
                                      SAVED
                                    </StatusBadge>
                                  </div>
                                </CellTooltip>
                              </div>
                            </div>
                          ) : displayContact.emailAvailable ? (
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="flex flex-col">
                                  <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mb-1">
                                    Primary Email
                                  </span>
                                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground font-bold tracking-wider">
                                    <Lock className="w-3.5 h-3.5 text-amber-500" />{" "}
                                    Verified work email available
                                  </div>
                                </div>
                                <StatusBadge variant="warning">
                                  LOCKED
                                </StatusBadge>
                              </div>
                              <CellTooltip tooltip="Spend 10 credits to reveal and save contact">
                                <Button
                                  onClick={() =>
                                    openUnlockModal(displayContact._id)
                                  }
                                  className="w-full h-10 bg-primary hover:bg-primary/90 text-white font-black text-[10px] uppercase tracking-widest rounded-xl gap-2 transition-all active:scale-[0.98] shadow-sm"
                                >
                                  <Key className="w-4 h-4" /> Unlock for 10
                                  credits
                                </Button>
                              </CellTooltip>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between">
                              <div className="flex flex-col">
                                <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mb-1">
                                  Primary Email
                                </span>
                                <div className="flex items-center gap-2 text-[10px] text-muted-foreground/50 font-bold tracking-wider">
                                  <Mail className="w-3.5 h-3.5 opacity-50" /> No
                                  verified email
                                </div>
                              </div>
                            </div>
                          )}
                          <div className="pt-4 border-t border-border/40 flex flex-col">
                            <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mb-1">
                              Office Location
                            </span>
                            <div className="flex items-center gap-2 text-sm text-foreground font-medium opacity-70">
                              <MapPin className="w-4 h-4 text-muted-foreground" />{" "}
                              {displayContact.location || "Unknown Location"}
                            </div>
                          </div>
                        </div>
                      </section>

                      <section className="space-y-4">
                        <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-primary/70 flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            <Globe className="w-3.5 h-3.5" /> Social Presence
                          </span>
                          {isDrawerHydrating && (
                            <div className="animate-pulse w-4 h-4 rounded-full bg-primary/20" />
                          )}
                        </h4>
                        {drawerData ? (
                          <div className="grid grid-cols-1 gap-3">
                            <Button
                              variant="outline"
                              className="h-12 w-full justify-start px-4 rounded-xl border-border/50 bg-card hover:bg-primary/5 hover:border-primary/30 transition-all font-bold text-xs gap-3 shadow-sm"
                            >
                              <Globe className="w-4 h-4 text-primary" />
                              {drawerData.socialProfiles?.linkedin
                                ? "LinkedIn Profile Found"
                                : "LinkedIn Profile Verified"}
                              <ChevronRight className="w-3.5 h-3.5 ml-auto opacity-30" />
                            </Button>
                          </div>
                        ) : (
                          <div className="h-12 border border-border/40 rounded-2xl bg-muted/10 animate-pulse"></div>
                        )}
                      </section>
                    </TabsContent>

                    <TabsContent
                      value="company"
                      className="space-y-8 mt-0 animate-in fade-in slide-in-from-bottom-2 duration-300"
                    >
                      <section className="space-y-4">
                        <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-primary/70 flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            <Building className="w-3.5 h-3.5" /> Organization
                            Context
                          </span>
                          {isDrawerHydrating && (
                            <div className="animate-pulse w-4 h-4 rounded-full bg-primary/20" />
                          )}
                        </h4>
                        {drawerData ? (
                          <div className="bg-muted/10 border border-border/50 rounded-2xl p-6 space-y-6 shadow-sm">
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/20 shadow-sm">
                                <Building className="w-6 h-6 text-primary" />
                              </div>
                              <div>
                                <div className="font-extrabold text-lg text-foreground tracking-tight">
                                  {drawerData.companyName ||
                                    displayContact.company ||
                                    "-"}
                                </div>
                                <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">
                                  Verified Business Entity
                                </div>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-6 pt-6 border-t border-border/40">
                              <div>
                                <div className="text-[9px] font-black text-muted-foreground uppercase tracking-widest mb-1.5">
                                  Industry Segment
                                </div>
                                <div className="text-xs font-bold text-foreground">
                                  {drawerData.companyMasterId?.industry ||
                                    drawerData.industry ||
                                    displayContact.industry ||
                                    "-"}
                                </div>
                              </div>
                              <div>
                                <div className="text-[9px] font-black text-muted-foreground uppercase tracking-widest mb-1.5">
                                  Workforce Scale
                                </div>
                                <div className="text-xs font-bold text-foreground">
                                  {drawerData.companyMasterId?.size ||
                                    drawerData.employeeCount ||
                                    "-"}
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="h-40 border border-border/40 rounded-2xl bg-muted/10 animate-pulse"></div>
                        )}
                      </section>
                    </TabsContent>

                    <TabsContent
                      value="activity"
                      className="space-y-8 mt-0 animate-in fade-in slide-in-from-bottom-2 duration-300"
                    >
                      <section className="space-y-4">
                        <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-primary/70 flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            <Activity className="w-3.5 h-3.5" /> Propensity
                            Intelligence
                          </span>
                          {isDrawerHydrating && (
                            <div className="animate-pulse w-4 h-4 rounded-full bg-primary/20" />
                          )}
                        </h4>
                        {drawerData ? (
                          <div className="bg-muted/10 border border-border/50 rounded-2xl p-6 space-y-6 shadow-sm">
                            <div className="flex items-center justify-between">
                              <div className="flex flex-col">
                                <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground">
                                  Buy Intent Score
                                </span>
                                <div className="text-xl font-black text-foreground mt-1 flex items-center gap-2">
                                  {drawerData.intentScore ||
                                    displayContact.confidenceScore ||
                                    0}
                                  <span className="text-[10px] font-bold text-muted-foreground tracking-normal">
                                    / 100
                                  </span>
                                </div>
                              </div>
                              <ScoreBadge
                                score={
                                  drawerData.intentScore ||
                                  displayContact.confidenceScore ||
                                  0
                                }
                              />
                            </div>

                            {drawerData.intentSignals?.length > 0 ? (
                              <div className="space-y-3 pt-6 border-t border-border/40">
                                <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground">
                                  Detected Signals
                                </span>
                                <div className="flex flex-wrap gap-2">
                                  {drawerData.intentSignals.map((sig, i) => (
                                    <StatusBadge
                                      key={i}
                                      variant="danger"
                                      className="font-black"
                                    >
                                      {sig}
                                    </StatusBadge>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <div className="pt-6 border-t border-border/40 text-center py-4">
                                <div className="text-xs font-bold text-muted-foreground opacity-40 uppercase tracking-widest">
                                  No matching intent tags found
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="h-32 border border-border/40 rounded-2xl bg-muted/10 animate-pulse"></div>
                        )}
                      </section>

                      <section className="space-y-4 pt-4 border-t border-border/30">
                        <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60 flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5" /> Integration Sync
                        </h4>
                        <div className="bg-muted/5 border border-dashed border-border/60 rounded-2xl p-8 flex flex-col items-center justify-center text-center">
                          <Database className="w-8 h-8 text-muted-foreground/20 mb-3" />
                          <div className="text-[11px] font-bold text-foreground/50 uppercase tracking-widest">
                            Sync Layer Pending
                          </div>
                          <div className="text-[10px] text-muted-foreground/40 mt-1 max-w-[220px] font-medium leading-relaxed">
                            Sequence history and CRM activity logs will hydrate
                            once the bridge is fully established.
                          </div>
                        </div>
                      </section>
                    </TabsContent>
                  </Tabs>
                </div>

                <div className="flex-none p-6 border-t border-border bg-card/80 backdrop-blur-sm flex items-center gap-3">
                  <Button
                    onClick={() => setIsSequenceModalOpen(true)}
                    className="flex-1 font-black text-[10px] uppercase tracking-widest bg-indigo-600 hover:bg-indigo-700 h-10 rounded-xl shadow-lg shadow-indigo-600/20 border-none transition-all active:scale-95 gap-2"
                  >
                    <Download className="w-4 h-4" /> Enroll contact
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 font-black text-[10px] uppercase tracking-widest h-10 rounded-xl border-border/60 hover:bg-muted transition-all shadow-sm"
                  >
                    Preview sequence
                  </Button>
                </div>
              </div>
            ) : null}
          </SheetContent>
        </Sheet>
      </div>
    </TooltipProvider>
  );
}
